'use client';

import Lottie from 'lottie-react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { use, useEffect } from 'react';
import { DialogDefaultProps } from '@/components/features/dialogs/infra/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { pushAnalyticsEvent } from '@/lib/analyticsClientSide';
import girlWithBell from '@/public/lottie/notifications/girl-with-bell.json';
import { PushNotificationContext } from '../context/PushNotificationContext';
export type NotificationScenario =
  | 'onboarding'
  | 'acceptMatch'
  | 'initiateMatch'
  | 'hlpRegistration'
  | 'multiDevices';

interface NotificationOptInDialogProps {
  scenario: NotificationScenario;
  userName?: string;
}

export default function NotificationOptInDialog({
  scenario,
  userName,
  open,
  onOpenChange,
}: NotificationOptInDialogProps & DialogDefaultProps) {
  const t = useTranslations('notifications');
  const [isMobile, isMobileLoading] = useIsMobile();

  const pushNotification = use(PushNotificationContext);
  if (!pushNotification) throw new Error('PushNotificationContext not found');
  const { subscribeToPush, isSubscribing } = pushNotification;
  const { data: session } = useSession();
  const accountType = session?.user?.accountType;

  useEffect(() => {
    if (scenario === 'multiDevices' && isMobileLoading) return;
    if (scenario === 'multiDevices' && !isMobileLoading && !isMobile && open) {
      onOpenChange(false);
      return;
    }
    if (open) {
      pushAnalyticsEvent('notification - showOptInPopup', {
        scenario,
      });
    }
  }, [open, scenario, isMobile, isMobileLoading, onOpenChange]);

  const handleSubscribe = async () => {
    pushAnalyticsEvent('notification - startSubscribed', {
      scenario,
    });
    await subscribeToPush();
    localStorage.setItem(`notificationDialogShown_${scenario}`, 'true');
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      localStorage.setItem(`notificationDialogShown_${scenario}`, 'true');
      pushAnalyticsEvent('notification - closedOptInPopup', {
        scenario,
      });
    }
    onOpenChange(newOpen);
  };

  if (
    scenario === 'multiDevices' &&
    (isMobileLoading || (!isMobileLoading && !isMobile))
  ) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader className='text-center'>
          <Lottie
            animationData={girlWithBell}
            loop={false}
            className='mx-auto w-[100px]'
          />

          <DialogTitle>
            {t(`dialogs.channelOptIn.${scenario}.title`, {
              accountType: accountType ?? '',
              userName: userName ?? '',
            })}
          </DialogTitle>
          <DialogDescription className='text-center text-gray-700'>
            {t(`dialogs.channelOptIn.${scenario}.description`, {
              accountType: accountType ?? '',
              userName: userName ?? '',
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='flex-col gap-6'>
          <Button
            onClick={handleSubscribe}
            className='w-full'
            size='medium'
            loading={isSubscribing}
          >
            {t(`dialogs.channelOptIn.confirm`)}
          </Button>
          <span className='text-center text-xs'>
            {t(`dialogs.channelOptIn.updatePreferences`)}
            <Link href='/notifications' className='underline'>
              {t(`dialogs.channelOptIn.notificationPage`)}
            </Link>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useShouldShowNotificationOptInDialog(
  scenario: NotificationScenario
) {
  const [isMobile, isMobileLoading] = useIsMobile();
  if (scenario === 'multiDevices') {
    if (isMobileLoading) {
      return { loading: true, shouldShow: false };
    } else {
      if (!isMobile) {
        return { loading: false, shouldShow: false };
      }
    }
  }
  if (typeof window === 'undefined')
    return { loading: false, shouldShow: false };

  const finalScenario = scenario ?? 'onboarding';
  const hasShownDialog = localStorage.getItem(
    `notificationDialogShown_${finalScenario}`
  );
  const pushPermission =
    typeof Notification !== 'undefined' ? Notification.permission : 'default';
  const shouldShow = pushPermission !== 'granted' && !hasShownDialog;

  return { loading: false, shouldShow };
}
